import React, { useState, useEffect } from 'react';
import {
  Button, TextField,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => {
  const surfaceConfig = theme.palette.surface[theme.palette.type];
  const containedConfig = surfaceConfig.button.contained;
  const outlinedConfig = surfaceConfig.button.outlined;
  const buttonConfig = theme.typography.button;

  return {
    root: {
      display: 'flex',
      flex: '0 0 auto',
      alignItems: 'center',
      justifyContent: 'left',
    },
    btnContained: {
      marginLeft: 0,
      paddingLeft: buttonConfig.paddingLeft,
      paddingRight: buttonConfig.paddingRight,
      borderRadius: buttonConfig.borderRadius,
      height: buttonConfig.height,
      backgroundColor: containedConfig.bgColor,
      '&:hover': {
        backgroundColor: containedConfig.hoveredBgColor,
      },
      '&:focus': {
        border: containedConfig.border,
        borderColor: containedConfig.borderColor,
        backgroundColor: containedConfig.bgColor,
      },
    },
    btnOutlined: {
      border: outlinedConfig.border,
      color: outlinedConfig.color,
      marginRight: theme.spacing(1),
      '&:hover': {
        border: outlinedConfig.hoveredBorder,
        backgroundColor: outlinedConfig.hoveredBgColor,
      },
      '&:focus': {
        border: outlinedConfig.selectedBorder,
        backgroundColor: outlinedConfig.selectedBgColor,
      },
    },
  };
});

function PromptDialog(props) {
  const {
    open,
    title,
    content,
    inputLabel,
    InputComponent,
    helperText,
    defaultValue,
    otherData,
    onValidate,
    onConfirm,
    onClose,
    closeButtonLabel,
    confirmButtonLabel,
  } = props;

  const [isSuccess, setIsSuccess] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const classes = useStyles();

  useEffect(() => {
    const success = onValidate(defaultValue);
    setIsSuccess(success);
    setInputValue(defaultValue);
  }, [defaultValue, onValidate]);

  const handleClose = (isConfirm = false) => {
    if (isConfirm) {
      onConfirm(inputValue);
    }
    onClose();
    setInputValue('');
  };

  const handleInputChange = (event) => {
    const { value } = event.target;
    onChange(value);
  };

  const onChange = (value) => {
    const success = onValidate(value);
    setInputValue(value);
    setIsSuccess(success);
  };

  const renderInputComponent = () => {
    const inputComponentProps = {
      margin: 'dense',
      autoFocus: true,
      fullWidth: true,
      helperText,
    };

    return (
      InputComponent ? (
        <InputComponent
          {...inputComponentProps}
          inputLabel={inputLabel}
          inputValue={inputValue}
          {...otherData}
          onChange={onChange}
        />
      ) : (
        <TextField
          {...inputComponentProps}
          label={inputLabel}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          variant="outlined"
        />
      )
    );
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={() => handleClose()} fullWidth aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {title}
          <span />
        </DialogTitle>
        <DialogContent className={classes.content}>
          <DialogContentText>
            {content}
          </DialogContentText>
          {renderInputComponent()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleClose()}
            variant="outlined"
            className={classes.btnOutlined}
          >
            {closeButtonLabel}
          </Button>
          <Button
            onClick={() => handleClose(true)}
            color="primary"
            variant="contained"
            disabled={!isSuccess}
            className={classes.btnContained}
          >
            {confirmButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

PromptDialog.defaultProps = {
  open: false,
  title: 'Note!',
  content: '',
  inputLabel: 'Name',
  InputComponent: null,
  helperText: undefined,
  defaultValue: undefined,
  otherData: {},
  onValidate: () => true,
  onConfirm: () => { },
  onClose: () => { },
  closeButtonLabel: 'Cancel',
  confirmButtonLabel: 'Confirm',
};

PromptDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  content: PropTypes.string,
  inputLabel: PropTypes.string,
  InputComponent: PropTypes.elementType,
  helperText: PropTypes.string,
  defaultValue: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  otherData: PropTypes.objectOf(PropTypes.any),
  onValidate: PropTypes.func,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  closeButtonLabel: PropTypes.string,
  confirmButtonLabel: PropTypes.string,
};

export default PromptDialog;
